import { useState } from "react";
import { useLocation } from "react-router-dom";
import { DashboardLayout } from "@layouts/index";
import BankAccountCard from "../../components/BankAccountCard";
import AddBankAccount from "components/AddBankAccount";
import { useSessionStorage } from "../../hooks";
import useHostWallet, {
  HostEarning,
  HostPayoutStatus,
  WalletEntry,
} from "../../hooks/useHostWallet";
import WithdrawModal from "./WithdrawModal";
import styles from "./HostWallet.module.css";

const naira = (value: number) =>
  `${value < 0 ? "-" : ""}₦${Math.abs(value || 0).toLocaleString("en-NG")}`;

const shortDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString("en-NG", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const PAYOUT_LABELS: Record<HostPayoutStatus, string> = {
  unpaid: "Awaiting payout",
  paid: "Paid",
  paid_outside_system: "Paid",
  cancelled: "Cancelled",
};

const PAYOUT_FILTERS = [
  { value: "", label: "All" },
  { value: "unpaid", label: "Awaiting payout" },
  { value: "paid", label: "Paid" },
  { value: "cancelled", label: "Cancelled" },
];

const ENTRY_LABELS: Record<WalletEntry["type"], string> = {
  Payment: "Booking earning",
  Withdrawal: "Withdrawal",
  Payout: "Payout to bank",
  CautionFee: "Caution fee",
  Adjustment: "Adjustment by Krent",
};

const isCredit = (entry: WalletEntry) =>
  entry.destination === "Wallet" && !/debit/i.test(entry.description || "");

function EarningRow({ earning }: { earning: HostEarning }) {
  const { hostPayout } = earning;
  const stay = `${shortDate(earning.arrivalDate)} – ${shortDate(earning.departureDate)}`;
  const partlyPaid = hostPayout.status === "unpaid" && (hostPayout.amountPaid || 0) > 0;

  return (
    <li className={styles["row"]}>
      <div className={styles["rowMain"]}>
        <p className={styles["rowTitle"]}>{earning.propertyTitle || "Property"}</p>
        <p className={styles["rowMeta"]}>
          {stay} · Ref {earning.bookingReference || earning.referenceId}
        </p>
        {partlyPaid && (
          <p className={styles["rowMeta"]}>
            {naira(hostPayout.amountPaid || 0)} paid · {naira(hostPayout.amount - (hostPayout.amountPaid || 0))} left
          </p>
        )}
        {hostPayout.status === "paid" && hostPayout.reference && (
          <p className={styles["rowMeta"]}>
            Paid {shortDate(hostPayout.paidAt)} · Bank ref {hostPayout.reference}
          </p>
        )}
        {hostPayout.reversedAt && hostPayout.status !== "cancelled" && (
          <p className={styles["rowWarning"]}>Booking cancelled after payout — this amount was taken back from your wallet.</p>
        )}
      </div>
      <div className={styles["rowSide"]}>
        <p className={styles["rowAmount"]}>{naira(hostPayout.amount)}</p>
        <span className={`${styles["chip"]} ${styles[`chip_${hostPayout.status}`]}`}>
          {partlyPaid ? "Partly paid" : PAYOUT_LABELS[hostPayout.status]}
        </span>
      </div>
    </li>
  );
}

function HistoryRow({ entry }: { entry: WalletEntry }) {
  const credit = isCredit(entry);
  return (
    <li className={styles["row"]}>
      <div className={styles["rowMain"]}>
        <p className={styles["rowTitle"]}>{ENTRY_LABELS[entry.type] || entry.type}</p>
        <p className={styles["rowMeta"]}>
          {shortDate(entry.createdAt)} · {entry.description}
        </p>
        {entry.status === "Rejected" && entry.otherDetails && (
          <p className={styles["rowWarning"]}>Rejected: {entry.otherDetails}</p>
        )}
        {entry.type === "Adjustment" && entry.otherDetails && (
          <p className={styles["rowMeta"]}>Reason: {entry.otherDetails}</p>
        )}
      </div>
      <div className={styles["rowSide"]}>
        <p className={`${styles["rowAmount"]} ${credit ? styles["credit"] : ""}`}>
          {credit ? "+" : "−"}
          {naira(entry.amount)}
        </p>
        {entry.status !== "Paid" && (
          <span className={`${styles["chip"]} ${styles[`chip_${entry.status.toLowerCase()}`]}`}>
            {entry.status === "Requested" ? "Pending" : entry.status}
          </span>
        )}
      </div>
    </li>
  );
}

export default function HostWallet() {
  const location = useLocation();
  const [user] = useSessionStorage("user", {});
  const [tab, setTab] = useState<"earnings" | "history">("earnings");
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showAddBank, setShowAddBank] = useState(false);
  const [isChangeRequest, setIsChangeRequest] = useState(false);

  const {
    wallet,
    walletError,
    loadingWallet,
    earnings,
    totals,
    earningsCount,
    earningsPage,
    setEarningsPage,
    payoutStatus,
    filterByPayoutStatus,
    loadingEarnings,
    history,
    historyPage,
    setHistoryPage,
    hasMoreHistory,
    loadingHistory,
    refresh,
  } = useHostWallet(user?.wallet);

  const available = wallet?.balance?.available ?? 0;
  const owesKrent = available < 0;
  const earningsPages = Math.max(Math.ceil(earningsCount / 10), 1);

  return (
    <DashboardLayout title="Wallet" subtitle="Your earnings and payouts from Krent">
      <div className={styles["page"]}>
        {!user?.wallet ? (
          <p className={styles["notice"]}>Your wallet is not set up yet. Verify your account to get one.</p>
        ) : (
          <>
            {walletError && <p className={styles["errorText"]}>{walletError}</p>}

            <BankAccountCard
              wallet={wallet || undefined}
              loading={loadingWallet}
              onLinkAccount={() => {
                setIsChangeRequest(false);
                setShowAddBank(true);
              }}
              onChangeAccount={() => {
                setIsChangeRequest(true);
                setShowAddBank(true);
              }}
            />

            {owesKrent && (
              <p className={styles["oweNotice"]} role="status">
                You owe Krent {naira(Math.abs(available))} from a booking cancelled after you were paid. It will be taken
                from your next earnings.
              </p>
            )}

            <section className={styles["stats"]} aria-label="Wallet summary">
              <div className={`${styles["stat"]} ${styles["statPrimary"]}`}>
                <p className={styles["statLabel"]}>Available</p>
                <p className={styles["statValue"]}>{loadingWallet ? "…" : naira(available)}</p>
                <p className={styles["statHint"]}>Includes bookings Krent hasn&apos;t paid you for yet.</p>
                <button
                  type="button"
                  className={styles["withdrawButton"]}
                  disabled={loadingWallet || available <= 0}
                  onClick={() => setShowWithdraw(true)}
                >
                  Withdraw
                </button>
              </div>
              <div className={styles["stat"]}>
                <p className={styles["statLabel"]}>Awaiting payout</p>
                <p className={styles["statValue"]}>{naira(totals.owed)}</p>
                <p className={styles["statHint"]}>Krent pays these to your bank account.</p>
              </div>
              <div className={styles["stat"]}>
                <p className={styles["statLabel"]}>Total earned</p>
                <p className={styles["statValue"]}>{naira(totals.earned)}</p>
              </div>
              <div className={styles["stat"]}>
                <p className={styles["statLabel"]}>Caution fees</p>
                <p className={styles["statValue"]}>{naira(totals.cautionFeesHeld)}</p>
                <p className={styles["statHint"]}>
                  Held by Krent from your guests. Report any damage before it&apos;s refunded to them.
                </p>
              </div>
            </section>

            <section className={styles["panel"]}>
              <div className={styles["tabs"]} role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "earnings"}
                  className={tab === "earnings" ? styles["tabActive"] : styles["tab"]}
                  onClick={() => setTab("earnings")}
                >
                  Earnings by booking
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "history"}
                  className={tab === "history" ? styles["tabActive"] : styles["tab"]}
                  onClick={() => setTab("history")}
                >
                  Wallet history
                </button>
              </div>

              {tab === "earnings" ? (
                <>
                  <div className={styles["filters"]}>
                    {PAYOUT_FILTERS.map((filter) => (
                      <button
                        key={filter.value}
                        type="button"
                        className={payoutStatus === filter.value ? styles["filterActive"] : styles["filter"]}
                        onClick={() => filterByPayoutStatus(filter.value)}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                  {loadingEarnings ? (
                    <p className={styles["notice"]}>Loading earnings…</p>
                  ) : earnings.length === 0 ? (
                    <p className={styles["notice"]}>No bookings here yet.</p>
                  ) : (
                    <ul className={styles["list"]}>
                      {earnings.map((earning) => (
                        <EarningRow key={earning.id} earning={earning} />
                      ))}
                    </ul>
                  )}
                  {earningsPages > 1 && (
                    <div className={styles["pager"]}>
                      <button type="button" disabled={earningsPage <= 1} onClick={() => setEarningsPage(earningsPage - 1)}>
                        Previous
                      </button>
                      <span>
                        Page {earningsPage} of {earningsPages}
                      </span>
                      <button
                        type="button"
                        disabled={earningsPage >= earningsPages}
                        onClick={() => setEarningsPage(earningsPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {loadingHistory ? (
                    <p className={styles["notice"]}>Loading history…</p>
                  ) : history.length === 0 ? (
                    <p className={styles["notice"]}>No wallet activity yet.</p>
                  ) : (
                    <ul className={styles["list"]}>
                      {history.map((entry) => (
                        <HistoryRow key={entry.id} entry={entry} />
                      ))}
                    </ul>
                  )}
                  {(historyPage > 1 || hasMoreHistory) && (
                    <div className={styles["pager"]}>
                      <button type="button" disabled={historyPage <= 1} onClick={() => setHistoryPage(historyPage - 1)}>
                        Previous
                      </button>
                      <span>Page {historyPage}</span>
                      <button type="button" disabled={!hasMoreHistory} onClick={() => setHistoryPage(historyPage + 1)}>
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        )}

        {showWithdraw && wallet && (
          <WithdrawModal
            walletId={user.wallet}
            available={available}
            hasPin={Boolean(user?.hasPIN)}
            hasBankAccount={Boolean(wallet.paymentMethod?.accountNumber)}
            onClose={() => setShowWithdraw(false)}
            onRequested={refresh}
          />
        )}
      </div>

      <AddBankAccount
        id={user?.wallet}
        type="personal"
        show={showAddBank}
        onClose={() => {
          setShowAddBank(false);
          refresh();
        }}
        onAccountSaved={() => {
          setShowAddBank(false);
          refresh();
        }}
        previousLocation={location.pathname + location.search}
        isChangeRequest={isChangeRequest}
      />
    </DashboardLayout>
  );
}
